import { useQuery } from '@apollo/client';
import { Combinator, Operator } from '@procureai/query-builder';
import { useTranslation } from 'react-i18next';

import { AgentProcessingStatus, ThreadStatus, ThreadVariant } from '../../../../Cockpit.types';
import {
  SvbrgCategoryType,
  SvbrgChatSessionVariant,
  SvbrgConversationEntityVariant,
  SvbrgThreadFilterField,
  SvbrgThreadOrderField,
  SvbrgIntakeStatus,
  SvbrgSortDirection,
} from '@/generated/gql/graphql';

import { THREAD_LIST_BY_CATEGORIES_QUERY } from './ThreadList.query.gql';

const intakeStatusMapping: Record<SvbrgIntakeStatus, AgentProcessingStatus> = {
  [SvbrgIntakeStatus.DRAFT]: AgentProcessingStatus.DRAFT,
  [SvbrgIntakeStatus.RELEASED]: AgentProcessingStatus.IN_PROGRESS,
  [SvbrgIntakeStatus.BLOCKED]: AgentProcessingStatus.PENDING,
  [SvbrgIntakeStatus.IN_PROGRESS]: AgentProcessingStatus.IN_PROGRESS,
  [SvbrgIntakeStatus.COMPLETED]: AgentProcessingStatus.READY,
  [SvbrgIntakeStatus.CANCELLED]: AgentProcessingStatus.COMPLETED,
};

const intakeVariantMapping: Record<SvbrgChatSessionVariant, ThreadVariant> = {
  [SvbrgChatSessionVariant.CHAT]: ThreadVariant.CONVERSATION,
  [SvbrgChatSessionVariant.REQUEST_CHAT]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_FORM]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_FORM_COMPLEX]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_INTENT_SELECTION]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_INTENT_SELECTION_COMPLEX]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_QUOTE]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_DLR]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_INTAKE_NUCLEAR_FORM]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.REQUEST_INTAKE_NUCLEAR_QUOTE]: ThreadVariant.GUIDED,
  [SvbrgChatSessionVariant.SOURCING_DEMAND]: ThreadVariant.SOURCING,
};

interface UseThreadCategoryProps {
  categories: SvbrgCategoryType[];
  textFilter?: string;
}

interface CategorizedThread {
  id: string;
  category: SvbrgCategoryType;
  status: ThreadStatus;
  variant: ThreadVariant;
  processingStatus: AgentProcessingStatus;
  summary: string;
  timestamp: Date;
  demandId?: string | null;
  conversationSettingsEntityId?: string | null;
  conversationSettingsEntityVariant?: SvbrgConversationEntityVariant | null;
}

interface UseThreadCategoryResult {
  categoryData: CategorizedThread[];
  loading: boolean;
}

const variantToCategoryMap: Record<SvbrgChatSessionVariant, SvbrgCategoryType> = {
  [SvbrgChatSessionVariant.REQUEST_FORM]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_FORM_COMPLEX]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_INTENT_SELECTION]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_INTENT_SELECTION_COMPLEX]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_QUOTE]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_CHAT]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_DLR]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_INTAKE_NUCLEAR_FORM]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.REQUEST_INTAKE_NUCLEAR_QUOTE]: SvbrgCategoryType.INTAKE,
  [SvbrgChatSessionVariant.CHAT]: SvbrgCategoryType.CONVERSATION,
  [SvbrgChatSessionVariant.SOURCING_DEMAND]: SvbrgCategoryType.SOURCING,
};

const mapThreadToCategorizedThread = (
  thread: {
    id: string;
    variant: SvbrgChatSessionVariant;
    status?: SvbrgIntakeStatus | null;
    summary: string | null;
    createdAt: string;
    demandId?: string | null;
    conversationSettingsEntityId?: string | null;
    conversationSettingsEntityVariant?: SvbrgConversationEntityVariant | null;
  },
  t: (key: string, options?: any) => string,
): CategorizedThread => {
  const variant = intakeVariantMapping[thread.variant];
  const category = variantToCategoryMap[thread.variant];

  return {
    id: thread.id,
    category,
    variant,
    status: ThreadStatus.READ, // TODO: Currently mark all entries as READ
    processingStatus: intakeStatusMapping[thread.status || SvbrgIntakeStatus.BLOCKED],
    summary: thread.summary || t(`${category}.fallback_title`, { sequenceNumber: '' }),
    timestamp: new Date(thread.createdAt),
    demandId: thread.demandId,
    conversationSettingsEntityId: thread.conversationSettingsEntityId,
    conversationSettingsEntityVariant: thread.conversationSettingsEntityVariant,
  };
};

export const useThreadCategory = ({ categories, textFilter = '' }: UseThreadCategoryProps): UseThreadCategoryResult => {
  const { t } = useTranslation('cockpit', { keyPrefix: 'AgentCategory' });

  const { data, loading } = useQuery(THREAD_LIST_BY_CATEGORIES_QUERY, {
    variables: {
      filter: {
        combinator: Combinator.AND,
        rules: [
          {
            field: SvbrgThreadFilterField.summary,
            operator: Operator.IN,
            value: [textFilter],
          },
        ],
      },
      order: [{ field: SvbrgThreadOrderField.createdAt, sort: SvbrgSortDirection.desc }],
      categories,
    },
    skip: categories.length === 0,
    fetchPolicy: 'cache-and-network',
  });

  const allThreads: CategorizedThread[] =
    data?.sevenbridges.threadList.nodes.map((thread) => mapThreadToCategorizedThread(thread, t)) ?? [];

  return {
    categoryData: allThreads,
    loading,
  };
};
